import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import {
  useCaptureSnapshot,
  useDeleteReport,
  useMeta,
  useRefreshReport,
  useReport,
  useReportTrend,
  useSchedules,
  useSetReportLayout,
  useIsAdmin,
  useUpdateReport,
  type Tile,
} from "@/api/queries";
import { Markdown } from "@/components/chat/Markdown";
import { AddTileDialog } from "@/components/reports/AddTileDialog";
import { AlertsDialog } from "@/components/reports/AlertsDialog";
import { CauseLensPanel } from "@/components/reports/CauseLensPanel";
import { ExplainBreachDialog } from "@/components/reports/ExplainBreachDialog";
import { ExportMenu } from "@/components/reports/ExportMenu";
import { AddTo } from "@/components/reports/PinMenus";
import { ScheduleDialog } from "@/components/reports/ScheduleDialog";
import { ShareDialog } from "@/components/reports/ShareDialog";
import { SnapshotHistory } from "@/components/reports/SnapshotHistory";
import { TileGrid } from "@/components/reports/TileGrid";
import { TileSqlDialog, TileSqlViewer } from "@/components/reports/TileSqlDialog";
import { TrendPanel } from "@/components/reports/TrendPanel";
import { Button } from "@/components/ui/Button";
import { Chip } from "@/components/ui/Chip";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { EditableTitle } from "@/components/ui/EditableTitle";
import { EmptyState } from "@/components/ui/EmptyState";
import { Spinner } from "@/components/ui/Spinner";
import {
  AlertIcon,
  BackIcon,
  CalendarIcon,
  BellIcon,
  CameraIcon,
  ChatIcon,
  LinkIcon,
  DatabaseIcon,
  PlusIcon,
  RefreshIcon,
  ReportIcon,
  SparkIcon,
  TrashIcon,
  TrendIcon,
} from "@/components/icons";
import { cn } from "@/lib/cn";
import { useRelativeTime } from "@/lib/time";

/** The three things a report can show below its narrative (M6). */
type ReportTab = "tiles" | "trend" | "cause";

/**
 * A report (PRD FR-7.4): the narrative header, the tile grid, refresh-on-open
 * and a refreshed-at stamp.
 *
 * **Refreshing on open is a client decision, not a side effect of the GET.**
 * A read that runs queries would make every reload of the API a write against
 * a customer database, and would make the M6 scheduler's job ambiguous. So the
 * page asks for the refresh once when it opens, renders the values it already
 * had while that runs, and stamps the result.
 */
export function ReportPage() {
  const { reportId } = useParams({ from: "/app/reports/$reportId" });
  const { explain } = useSearch({ from: "/app/reports/$reportId" });
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();
  const { data: report, isLoading } = useReport(reportId);
  const refresh = useRefreshReport(reportId);
  const layout = useSetReportLayout(reportId);
  const remove = useDeleteReport();
  const rename = useUpdateReport(reportId);
  const stamp = useRelativeTime(report?.last_refreshed_at);

  const [editing, setEditing] = useState<Tile | null>(null);
  const [viewing, setViewing] = useState<Tile | null>(null);
  const [tab, setTab] = useState<ReportTab>("tiles");
  const [scheduling, setScheduling] = useState(false);
  // ALERT-2: opened from the header, or from a trend series with that metric.
  const [alerting, setAlerting] = useState<{ key: string | null } | null>(null);
  // ALERT-3: the event an alert email asked to explain, or one picked in the
  // alerts dialog — and the run it started, which the Cause Lens tab streams.
  const [explaining, setExplaining] = useState<string | null>(explain ?? null);
  const [lensRun, setLensRun] = useState<{
    runId: string;
    threadId: string;
    basis: string;
  } | null>(null);
  const [adding, setAdding] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [sharing, setSharing] = useState(false);

  const { data: meta } = useMeta();
  const { data: schedules } = useSchedules();
  const snapshot = useCaptureSnapshot(reportId);
  // Only fetched when the tab is open: a trend reads every snapshot a report
  // has, and a report page that loaded ninety of them to show a grid would be
  // paying for a view nobody opened.
  const { data: trend, isLoading: trendLoading } = useReportTrend(
    reportId,
    tab === "trend" || alerting !== null,
  );
  const schedule = useMemo(
    () =>
      (schedules ?? []).find(
        (entry) => entry.owner_kind === "report" && entry.owner_id === reportId,
      ) ?? null,
    [schedules, reportId],
  );

  // Refresh-on-open, once per report (PRD FR-7.4).
  const opened = useRef<string | null>(null);
  useEffect(() => {
    if (!report || opened.current === report.id) return;
    opened.current = report.id;
    if (report.tiles.some((tile) => tile.query_backed)) refresh.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report?.id]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }
  if (!report) {
    return (
      <div className="px-6 py-10">
        <EmptyState
          icon={<ReportIcon className="size-6" />}
          title="No such report"
          detail="It may have been deleted."
          action={
            <Link to="/reports" className="text-accent hover:underline">
              Back to reports
            </Link>
          }
        />
      </div>
    );
  }

  const failures = report.tiles.filter(
    (tile) => tile.refresh_status === "error" || tile.refresh_status === "blocked",
  );

  return (
    <div className="mx-auto flex h-full min-h-0 w-full max-w-[100rem] flex-col">
      <header className="border-border flex flex-col gap-3 border-b px-6 py-4">
        <div className="flex flex-wrap items-start gap-3">
          <Link
            to="/reports"
            className="text-ink-secondary hover:text-ink hover:bg-hover rounded-element motion-fast mt-0.5 inline-flex size-8 items-center justify-center"
            aria-label="Back to reports"
          >
            <BackIcon className="size-4" />
          </Link>
          {/* A floor, not `min-w-0`: the action row is allowed to wrap (the
              header is `flex-wrap`) but the name is not allowed to shrink to
              nothing. At `min-w-0` this title truncated to about eight
              characters on a 1280px window, and the rename field inherited the
              same 121px. */}
          <div className="min-w-56 flex-1">
            <EditableTitle
              as="h1"
              className="type-h2 text-ink"
              value={report.title}
              placeholder="Untitled report"
              label="report"
              onSave={(title) => rename.mutateAsync({ title })}
            />
            {report.question ? (
              <p className="type-support text-ink-secondary mt-1 truncate">“{report.question}”</p>
            ) : null}
          </div>

          <Button
            variant="primary"
            icon={<PlusIcon className="size-4" />}
            onClick={() => setAdding(true)}
          >
            Add tile
          </Button>
          <Button
            icon={<RefreshIcon className="size-4" />}
            loading={refresh.isPending}
            onClick={() => refresh.mutate()}
          >
            Refresh
          </Button>
          <Button
            icon={<CameraIcon className="size-4" />}
            loading={snapshot.isPending}
            onClick={() => snapshot.mutate({ refresh: true })}
          >
            Snapshot
          </Button>
          {/* Who gets emailed what is the Admin tier's (ACC-2). */}
          {isAdmin ? (
            <Button
              icon={<CalendarIcon className="size-4" />}
              onClick={() => setScheduling(true)}
            >
              {schedule ? "Schedule…" : "Schedule"}
            </Button>
          ) : null}
          {isAdmin ? (
            <Button
              icon={<BellIcon className="size-4" />}
              onClick={() => setAlerting({ key: null })}
            >
              Alerts
            </Button>
          ) : null}
          <ExportMenu base={`/api/reports/${report.id}`} html />
          <Button icon={<LinkIcon className="size-4" />} onClick={() => setSharing(true)}>
            Share
          </Button>
          <AddTo reportId={report.id} label="Add to dashboard…" />
          <Button icon={<TrashIcon className="size-4" />} onClick={() => setConfirmingDelete(true)}>
            Delete
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          {report.connection_name ? (
            <Chip tone="neutral" icon={<DatabaseIcon className="size-3" />}>
              {report.connection_name}
            </Chip>
          ) : null}
          {report.model_label ? <Chip tone="neutral">{report.model_label}</Chip> : null}
          <span className="type-caption text-ink-disabled">
            {refresh.isPending
              ? "refreshing…"
              : stamp
                ? `refreshed ${stamp}`
                : report.source_message_id
                  ? "showing the values this answer produced"
                  : // A report built by hand was never an answer. Its tiles ran
                    // when they were added, which is what they are showing.
                    "showing the values each tile returned when it was added"}
          </span>
          {report.refresh && report.refresh.saved_queries > 0 ? (
            <span className="type-caption text-ink-disabled">
              · {report.refresh.queries} quer{report.refresh.queries === 1 ? "y" : "ies"} for{" "}
              {report.refresh.tiles} tiles
            </span>
          ) : null}
          {schedule ? (
            <Chip tone={schedule.enabled ? "success" : "neutral"} icon={<CalendarIcon className="size-3" />}>
              {schedule.description}
              {schedule.enabled ? "" : " · off"}
            </Chip>
          ) : null}
          {report.source_thread_id ? (
            <Link
              to="/chat/$threadId"
              params={{ threadId: report.source_thread_id }}
              className="type-caption text-ink-disabled hover:text-ink inline-flex items-center gap-1"
            >
              <ChatIcon className="size-3" />
              the conversation
            </Link>
          ) : null}
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
        <div className="mb-5 flex flex-col gap-4">
          {report.summary ? (
            <div className="type-body text-ink max-w-3xl">
              <Markdown>{report.summary}</Markdown>
            </div>
          ) : null}

          {report.insights.length ? (
            <ul className="flex max-w-3xl flex-col gap-1.5">
              {report.insights.map((insight) => (
                <li key={insight} className="type-support text-ink-secondary flex gap-2">
                  <SparkIcon className="text-accent mt-0.5 size-3.5 shrink-0" />
                  <span>{insight}</span>
                </li>
              ))}
            </ul>
          ) : null}

          {report.sources.length ? (
            <div className="flex flex-wrap items-center gap-1.5">
              <DatabaseIcon className="text-ink-disabled size-3.5" />
              {report.sources.map((source) => (
                <Chip key={source} tone="neutral">
                  {source}
                </Chip>
              ))}
            </div>
          ) : null}

          {report.warnings.map((warning) => (
            <p
              key={warning}
              className="bg-warning-subtle text-warning type-support rounded-element flex max-w-3xl items-start gap-2 px-3 py-2"
            >
              <AlertIcon className="mt-0.5 size-3.5 shrink-0" />
              <span>{warning}</span>
            </p>
          ))}

          {failures.length ? (
            <p className="bg-error-subtle text-error type-support rounded-element flex max-w-3xl items-start gap-2 px-3 py-2">
              <AlertIcon className="mt-0.5 size-3.5 shrink-0" />
              <span>
                {failures.length} tile{failures.length === 1 ? "" : "s"} could not be refreshed.
                {" "}The values below are the last ones that worked.
              </span>
            </p>
          ) : null}
        </div>

        <nav className="border-border mb-4 flex gap-1 border-b" aria-label="Report views">
          {(
            [
              ["tiles", "Report", <ReportIcon key="i" className="size-3.5" />],
              ["trend", "Trend", <TrendIcon key="i" className="size-3.5" />],
              ["cause", "Cause Lens", <SparkIcon key="i" className="size-3.5" />],
            ] as const
          ).map(([key, label, glyph]) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={cn(
                "type-label motion-fast -mb-px inline-flex items-center gap-1.5 border-b-2 px-3 py-2",
                tab === key
                  ? "border-accent text-accent"
                  : "text-ink-secondary hover:text-ink border-transparent",
              )}
            >
              {glyph}
              {label}
            </button>
          ))}
        </nav>

        {tab === "tiles" ? (
          report.tiles.length ? (
            <TileGrid
              tiles={report.tiles}
              links={report.links ?? []}
              showConnections={false}
              saving={layout.isPending}
              onLayout={(frames) => layout.mutate(frames)}
              onEditSql={setEditing}
              onViewSql={setViewing}
            />
          ) : (
            <EmptyState
              icon={<ReportIcon className="size-6" />}
              title="This report has no tiles"
              detail="Add one from a statement you write here, or use “Add to…” on any answer in a conversation."
              action={
                <Button variant="primary" onClick={() => setAdding(true)}>
                  Add tile
                </Button>
              }
            />
          )
        ) : null}

        {tab === "trend" ? (
          <div className="flex flex-col gap-5">
            <TrendPanel
              trend={trend}
              loading={trendLoading}
              locale={meta?.locale ?? "en-US"}
              onAddAlert={isAdmin ? (series) => setAlerting({ key: series.key }) : undefined}
            />
            {/* Beside the trend, not inside it: a report whose tiles are all
                tables has snapshots and no trendable metric, and its history
                is exactly what that reader came for. */}
            <SnapshotHistory reportId={report.id} enabled={tab === "trend"} />
          </div>
        ) : null}

        {tab === "cause" ? (
          <CauseLensPanel
            reportId={report.id}
            connectionId={report.connection_id}
            canRun={Boolean(report.connection_id)}
            attach={lensRun}
          />
        ) : null}
      </div>

      <AddTileDialog
        open={adding}
        onClose={() => setAdding(false)}
        owner={{ kind: "report", id: report.id }}
        defaultConnectionId={report.connection_id}
      />
      <ConfirmDialog
        open={confirmingDelete}
        onClose={() => setConfirmingDelete(false)}
        loading={remove.isPending}
        title="Delete this report?"
        description={<>“{report.title || "Untitled report"}” will be deleted.</>}
        consequence={
          <>
            Its {report.tile_count} tile{report.tile_count === 1 ? "" : "s"}, its snapshots and any
            schedule that refreshes it go with it.
            {report.source_thread_id ? " The conversation it came from is not touched." : ""}
          </>
        }
        onConfirm={() =>
          remove.mutate(report.id, {
            onSuccess: () => {
              setConfirmingDelete(false);
              void navigate({ to: "/reports" });
            },
          })
        }
      />
      <TileSqlDialog tile={editing} open={Boolean(editing)} onClose={() => setEditing(null)} />
      <TileSqlViewer tile={viewing} open={Boolean(viewing)} onClose={() => setViewing(null)} />
      {alerting ? (
        <AlertsDialog
          open
          onClose={() => setAlerting(null)}
          subject={{ kind: "report", id: report.id }}
          subjectName={report.title || "Untitled report"}
          series={(trend?.series ?? []).filter((entry) => entry.points.length > 0)}
          seriesLoading={trendLoading}
          initialKey={alerting.key}
          onExplain={(eventId) => {
            setAlerting(null);
            setExplaining(eventId);
          }}
        />
      ) : null}
      <ExplainBreachDialog
        reportId={report.id}
        eventId={explaining}
        onClose={() => {
          setExplaining(null);
          if (explain) void navigate({ to: "/reports/$reportId", params: { reportId }, search: {} });
        }}
        onStarted={(started) => {
          setExplaining(null);
          setLensRun({ runId: started.run_id, threadId: started.thread_id, basis: started.basis });
          setTab("cause");
          if (explain) void navigate({ to: "/reports/$reportId", params: { reportId }, search: {} });
        }}
      />
      <ShareDialog
        open={sharing}
        onClose={() => setSharing(false)}
        kind="report"
        subjectId={report.id}
        subjectName={report.title || "Untitled report"}
      />
      <ScheduleDialog
        open={scheduling}
        onClose={() => setScheduling(false)}
        ownerKind="report"
        ownerId={report.id}
        schedule={schedule}
      />
    </div>
  );
}
